import React, { useEffect, useRef, useState } from "react";

/**
 * AG Grid cell editor for Task Manager DATE columns, capturing date + time.
 *
 * A plain native <input type="datetime-local"> — the datetime equivalent of the
 * date-only editor used before (agDateStringCellEditor) — so Enter commits the
 * value the same way. Uses AG Grid's reactive cell-editor API (props.onValueChange
 * + props.stopEditing); the legacy getValue()/useImperativeHandle path is NOT
 * called by ag-grid-react v31+ and was why edits never saved.
 *
 * Everything is Pacific time end-to-end so the round-trip is stable in any
 * browser timezone: the datetime-local fields the user types are interpreted as
 * PST wall-clock (pstWallToEpoch), stored as an epoch-milliseconds STRING, and
 * both the editor (parseStoredToInput) and the grid display (formatDateTimeCell)
 * render the PST wall-clock of that instant. This matches how SEC Central stores
 * deliverable due dates (epoch ms). Legacy date-only values ("YYYY-MM-DD") from
 * the old editor are read as PST midnight.
 */

const PST_TZ = "America/Los_Angeles";
const pad = (n) => String(n).padStart(2, "0");

// The Y/M/D H:m:s a given instant shows on the clock in Pacific time.
const partsInPst = (date) => {
  const dtf = new Intl.DateTimeFormat("en-US", {
    timeZone: PST_TZ,
    hourCycle: "h23",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
  const map = {};
  for (const p of dtf.formatToParts(date)) {
    if (p.type !== "literal") map[p.type] = p.value;
  }
  return {
    year: Number(map.year),
    month: Number(map.month),
    day: Number(map.day),
    hour: Number(map.hour),
    minute: Number(map.minute),
    second: Number(map.second || 0),
  };
};

// PST wall-clock fields -> epoch ms. Derives the zone offset (DST-aware) by
// round-tripping a UTC guess through the PST clock and correcting by the delta.
const pstWallToEpoch = (y, mo, d, h, mi) => {
  const utcGuess = Date.UTC(y, mo - 1, d, h, mi);
  const p = partsInPst(new Date(utcGuess));
  const asUtc = Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second);
  return utcGuess - (asUtc - utcGuess);
};

// Any stored form -> epoch ms (number) or null.
const storedToEpochMs = (value) => {
  if (value === null || value === undefined || value === "") return null;
  const s = String(value).trim();
  if (/^\d{10,}$/.test(s)) return parseInt(s, 10); // epoch ms
  const m = /^(\d{4})-(\d{2})-(\d{2})(?:T(\d{2}):(\d{2}))?$/.exec(s); // ISO date[/time]
  if (m) {
    return pstWallToEpoch(+m[1], +m[2], +m[3], +(m[4] || 0), +(m[5] || 0));
  }
  const t = new Date(s).getTime();
  return Number.isNaN(t) ? null : t;
};

// Stored value -> the "YYYY-MM-DDTHH:mm" (PST) string a datetime-local expects.
const parseStoredToInput = (value) => {
  const ms = storedToEpochMs(value);
  if (ms === null) return "";
  const p = partsInPst(new Date(ms));
  return `${p.year}-${pad(p.month)}-${pad(p.day)}T${pad(p.hour)}:${pad(p.minute)}`;
};

// datetime-local string (PST wall time) -> stored epoch-ms string.
const inputToStored = (local) => {
  if (!local) return "";
  const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/.exec(local);
  if (!m) return "";
  return String(pstWallToEpoch(+m[1], +m[2], +m[3], +m[4], +m[5]));
};

/**
 * Format a stored value for display in Pacific time. Empty renders as "";
 * unparseable values render as the raw value.
 */
export const formatDateTimeCell = (value) => {
  const ms = storedToEpochMs(value);
  if (ms === null) return value === null || value === undefined ? "" : String(value).trim();
  return new Date(ms).toLocaleString("en-US", {
    timeZone: PST_TZ,
    year: "numeric",
    month: "numeric",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
};

// A JS Date (or null) for AG Grid's date filter to compare against.
export const storedValueToDate = (value) => {
  const ms = storedToEpochMs(value);
  return ms === null ? null : new Date(ms);
};

const DateTimeCellEditor = (props) => {
  const [display, setDisplay] = useState(parseStoredToInput(props.value));
  const inputRef = useRef(null);

  // Focus the input as soon as the editor mounts, like the native editor did.
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const handleChange = (e) => {
    const local = e.target.value;
    setDisplay(local);
    // Reactive API: push the committed (epoch-ms) value to AG Grid on every change
    // so stopEditing (Enter / blur / tab) persists the latest value.
    props.onValueChange?.(inputToStored(local));
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      props.stopEditing?.();
    } else if (e.key === "Escape") {
      props.stopEditing?.(true);
    }
  };

  return (
    <input
      ref={inputRef}
      type="datetime-local"
      className="ag-input-field-input"
      value={display}
      onChange={handleChange}
      onKeyDown={onKeyDown}
      style={{ width: "100%", height: "100%", border: "none", padding: "0 8px" }}
    />
  );
};

export default DateTimeCellEditor;
